import { useEffect } from 'react';

/**
 * GlobalTableScrollbars — app-wide always-visible custom horizontal scrollbars.
 *
 * macOS overlay scrollbars stay hidden until the user scrolls, so wide tables
 * inside `.ggc-table-frame--scroll` look truncated with no visible scroll
 * affordance (native bars also vary by OS). This mounts once and attaches a
 * fixed-position custom horizontal scrollbar to every overflowing frame.
 *
 * Design notes:
 * - Additive: never touches the frame's DOM/overflow, so sticky headers and
 *   vertical scrolling are unaffected. The bar is a `position: fixed` overlay
 *   pinned to the bottom edge of the frame (clamped to the viewport bottom).
 * - Synced to the frame's native horizontal scroll; dragging the thumb (or
 *   clicking the track) sets `frame.scrollLeft`.
 * - Frames already wrapped by <HScrollWrap> (`.h-scroll-wrap-inner`, which ships
 *   its own custom bar) or the influencer dashboard table (`.dashboard-table-wrap`,
 *   which ships its own bar via InfluencerTable) are skipped to avoid double bars.
 */

const BAR_HEIGHT = 10;

interface BarEntry {
  bar: HTMLDivElement;
  thumb: HTMLDivElement;
  cleanup: () => void;
}

export function GlobalTableScrollbars() {
  useEffect(() => {
    const entries = new Map<HTMLElement, BarEntry>();
    let rafId: number | null = null;
    let scanScheduled = false;

    // The nearest ancestor of a <table> that can scroll horizontally. This is
    // structural (computed overflow-x), so it covers `.ggc-table-frame--scroll`
    // AND any table wrapped in a bare `overflow-x-auto` container. HScrollWrap
    // (`.h-scroll-wrap-inner`) and the dashboard table (`.dashboard-table-wrap`)
    // ship their own custom bar, so they're explicitly skipped here even though
    // both are `overflow-x: auto` (needed for native touch panning on mobile).
    const findScrollContainer = (table: Element): HTMLElement | null => {
      let el = table.parentElement;
      while (el && el !== document.body) {
        if (el.classList.contains('h-scroll-wrap-inner') || el.classList.contains('dashboard-table-wrap')) return null;
        const ox = getComputedStyle(el).overflowX;
        if (ox === 'auto' || ox === 'scroll') return el;
        el = el.parentElement;
      }
      return null;
    };

    const ro = new ResizeObserver(() => schedule());

    const ensureBar = (frame: HTMLElement): BarEntry => {
      const existing = entries.get(frame);
      if (existing) return existing;

      const bar = document.createElement('div');
      bar.className = 'gtbl-hbar';
      const thumb = document.createElement('div');
      thumb.className = 'gtbl-hbar-thumb';
      bar.appendChild(thumb);
      document.body.appendChild(bar);

      let dragging = false;
      let startX = 0;
      let startScroll = 0;

      const onThumbDown = (e: MouseEvent) => {
        dragging = true;
        startX = e.clientX;
        startScroll = frame.scrollLeft;
        document.body.style.userSelect = 'none';
        e.preventDefault();
        e.stopPropagation();
      };
      const onMove = (e: MouseEvent) => {
        if (!dragging) return;
        const maxScroll = frame.scrollWidth - frame.clientWidth;
        const maxThumb = bar.clientWidth - thumb.offsetWidth;
        if (maxThumb <= 0) return;
        frame.scrollLeft = startScroll + ((e.clientX - startX) / maxThumb) * maxScroll;
        schedule();
      };
      const onUp = () => {
        if (!dragging) return;
        dragging = false;
        document.body.style.userSelect = '';
      };
      // Click on the track (not the thumb) jumps toward that position.
      const onTrackDown = (e: MouseEvent) => {
        if (e.target === thumb) return;
        const rect = bar.getBoundingClientRect();
        const maxScroll = frame.scrollWidth - frame.clientWidth;
        const ratio = (e.clientX - rect.left) / rect.width;
        frame.scrollLeft = Math.max(0, Math.min(maxScroll, ratio * maxScroll));
        schedule();
      };

      thumb.addEventListener('mousedown', onThumbDown);
      bar.addEventListener('mousedown', onTrackDown);
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      frame.addEventListener('scroll', schedule, { passive: true });
      ro.observe(frame);

      const entry: BarEntry = {
        bar,
        thumb,
        cleanup: () => {
          thumb.removeEventListener('mousedown', onThumbDown);
          bar.removeEventListener('mousedown', onTrackDown);
          document.removeEventListener('mousemove', onMove);
          document.removeEventListener('mouseup', onUp);
          frame.removeEventListener('scroll', schedule);
          ro.unobserve(frame);
          bar.remove();
        },
      };
      entries.set(frame, entry);
      return entry;
    };

    const positionBar = (frame: HTMLElement, entry: BarEntry) => {
      const { bar, thumb } = entry;
      const overflow = frame.scrollWidth - frame.clientWidth;
      const rect = frame.getBoundingClientRect();
      const vh = window.innerHeight;
      const offscreen = rect.bottom <= 0 || rect.top >= vh || rect.width === 0;
      if (overflow <= 1 || offscreen) {
        bar.style.display = 'none';
        return;
      }
      bar.style.display = 'block';
      // Pin to the bottom of the frame, clamped so it stays reachable in-viewport.
      const top = Math.min(rect.bottom - BAR_HEIGHT, vh - BAR_HEIGHT);
      bar.style.left = `${rect.left}px`;
      bar.style.top = `${Math.max(0, top)}px`;
      bar.style.width = `${rect.width}px`;

      const trackW = rect.width;
      const thumbW = Math.max(trackW * (frame.clientWidth / frame.scrollWidth), 30);
      const maxThumb = trackW - thumbW;
      const left = overflow > 0 ? (frame.scrollLeft / overflow) * maxThumb : 0;
      thumb.style.width = `${thumbW}px`;
      thumb.style.transform = `translateX(${left}px)`;
    };

    const update = () => {
      rafId = null;
      entries.forEach((entry, frame) => {
        if (!frame.isConnected) {
          entry.cleanup();
          entries.delete(frame);
          return;
        }
        positionBar(frame, entry);
      });
    };
    function schedule() {
      if (rafId == null) rafId = requestAnimationFrame(update);
    }

    const scan = () => {
      scanScheduled = false;
      const containers = new Set<HTMLElement>();
      document.querySelectorAll('table').forEach((tbl) => {
        const c = findScrollContainer(tbl);
        if (c) containers.add(c);
      });
      containers.forEach((c) => ensureBar(c));
      schedule();
    };
    const scheduleScan = () => {
      if (scanScheduled) return;
      scanScheduled = true;
      requestAnimationFrame(scan);
    };

    const mo = new MutationObserver(scheduleScan);
    mo.observe(document.body, { childList: true, subtree: true });
    // capture:true so scrolling any ancestor (page scroll) repositions the bars.
    window.addEventListener('scroll', schedule, { passive: true, capture: true });
    window.addEventListener('resize', schedule);
    scan();

    return () => {
      mo.disconnect();
      ro.disconnect();
      window.removeEventListener('scroll', schedule, { capture: true } as EventListenerOptions);
      window.removeEventListener('resize', schedule);
      entries.forEach((entry) => entry.cleanup());
      entries.clear();
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, []);

  return null;
}
